import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import YoutubeExtension from '@tiptap/extension-youtube'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Bold, Italic, Heading2, Heading3, Heading4, List, ListOrdered,
  Quote, Link2, Minus, Undo, Redo, Unlink, ImagePlus, Youtube, Code, ListTree, Table as TableIcon,
} from 'lucide-react'
import Timeline from '@/components/tiptap/TimelineNode'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'

type Variant = 'basic' | 'full'
const IMAGE_RESIZE_MIN_WIDTH = 80

type Props = {
  content: string
  onChange: (html: string) => void
  variant?: Variant
  enableImageResize?: boolean
}

export default function RichTextEditor({
  content,
  onChange,
  variant = 'basic',
  enableImageResize = false,
}: Props) {
  const isFull = variant === 'full'

  const extensions = [
    StarterKit.configure({
      heading: { levels: isFull ? [2, 3, 4] : [2, 3] },
      codeBlock: isFull ? {} : false,
    }),
    Link.configure({ openOnClick: false }),
    ...(isFull
      ? [
          Image.configure({
            inline: false,
            allowBase64: false,
            resize: enableImageResize
              ? {
                  enabled: true,
                  directions: ['top-right', 'top-left', 'bottom-right', 'bottom-left'],
                  minWidth: IMAGE_RESIZE_MIN_WIDTH,
                  minHeight: 80,
                  alwaysPreserveAspectRatio: true,
                }
              : false,
          }),
          YoutubeExtension.configure({
            controls: true,
            nocookie: true,
            HTMLAttributes: { class: 'aspect-video w-full rounded-md' },
          }),
          Table.configure({ resizable: false }),
          TableRow,
          TableHeader,
          TableCell,
          Timeline,
        ]
      : []),
  ]

  const editor = useEditor({
    extensions,
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    immediatelyRender: false,
  })

  if (!editor) return null

  const editorImageClass = enableImageResize
    ? '[&_.tiptap_img]:rounded-none [&_.tiptap_img]:shadow-none'
    : '[&_.tiptap_img]:rounded-md'

  return (
    <div className="rounded-md border border-input">
      <Toolbar editor={editor} variant={variant} enableImageResize={enableImageResize} />
      <EditorContent
        editor={editor}
        className={`prose ${isFull ? 'prose-base' : 'prose-sm'} max-w-none p-4 prose-th:border prose-th:border-black/10 prose-th:bg-muted/40 prose-th:px-3 prose-th:py-2 prose-td:border prose-td:border-black/10 prose-td:px-3 prose-td:py-2 ${
          isFull ? 'min-h-[600px] [&_.tiptap]:min-h-[580px]' : 'min-h-[300px] [&_.tiptap]:min-h-[280px]'
        } ${
          enableImageResize ? 'tiptap-image-resize' : ''
        } ${editorImageClass} focus-within:outline-none [&_.tiptap]:outline-none`}
      />
    </div>
  )
}

function ToolbarButton({
  onClick,
  active = false,
  children,
  title,
}: {
  onClick: () => void
  active?: boolean
  children: React.ReactNode
  title: string
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={`h-8 w-8 p-0 ${active ? 'bg-muted text-foreground' : 'text-muted-foreground'}`}
      onClick={onClick}
      title={title}
    >
      {children}
    </Button>
  )
}

function Toolbar({
  editor,
  variant,
  enableImageResize,
}: {
  editor: ReturnType<typeof useEditor>
  variant: Variant
  enableImageResize: boolean
}) {
  const [linkUrl, setLinkUrl] = useState('')
  const [linkOpen, setLinkOpen] = useState(false)
  const [ytUrl, setYtUrl] = useState('')
  const [ytOpen, setYtOpen] = useState(false)
  const [imageUploading, setImageUploading] = useState(false)
  const [imageError, setImageError] = useState<string | null>(null)
  const [imageWidth, setImageWidth] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isFull = variant === 'full'

  const syncImageWidth = useCallback(() => {
    if (!enableImageResize || !editor?.isActive('image')) {
      setImageWidth('')
      return
    }

    const width = parseImageDimension(editor.getAttributes('image').width)
    setImageWidth(width ? String(width) : '')
  }, [editor, enableImageResize])

  useEffect(() => {
    if (!enableImageResize || !editor) return undefined

    syncImageWidth()
    editor.on('selectionUpdate', syncImageWidth)
    editor.on('update', syncImageWidth)

    return () => {
      editor.off('selectionUpdate', syncImageWidth)
      editor.off('update', syncImageWidth)
    }
  }, [editor, enableImageResize, syncImageWidth])

  const setLink = useCallback(() => {
    if (!editor || !linkUrl) return
    editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run()
    setLinkUrl('')
    setLinkOpen(false)
  }, [editor, linkUrl])

  const removeLink = useCallback(() => {
    if (!editor) return
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
  }, [editor])

  const insertYouTube = useCallback(() => {
    if (!editor || !ytUrl) return
    if (!parseYouTubeId(ytUrl)) return
    editor.commands.setYoutubeVideo({ src: ytUrl, width: 640, height: 360 })
    setYtUrl('')
    setYtOpen(false)
  }, [editor, ytUrl])

  const uploadImage = useCallback(
    async (file: File) => {
      if (!editor) return
      setImageUploading(true)
      setImageError(null)
      try {
        const form = new FormData()
        form.append('file', file)
        const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        const res = await fetch('/admin/blog_images', {
          method: 'POST',
          body: form,
          headers: { 'X-CSRF-Token': csrf, Accept: 'application/json' },
          credentials: 'same-origin',
        })
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}))
          throw new Error(payload.error || 'Upload failed')
        }
        const { url } = (await res.json()) as { url: string }
        editor.chain().focus().setImage({ src: url, alt: file.name }).run()
      } catch (err) {
        setImageError(err instanceof Error ? err.message : 'Upload failed')
      } finally {
        setImageUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
    },
    [editor],
  )

  if (!editor) return null

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-input bg-muted/30 p-1.5">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="Bold"
      >
        <Bold className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="Italic"
      >
        <Italic className="size-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        <Heading2 className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        <Heading3 className="size-4" />
      </ToolbarButton>
      {isFull && (
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          active={editor.isActive('heading', { level: 4 })}
          title="Heading 4"
        >
          <Heading4 className="size-4" />
        </ToolbarButton>
      )}

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="Ordered List"
      >
        <ListOrdered className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive('blockquote')}
        title="Blockquote"
      >
        <Quote className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal Rule"
      >
        <Minus className="size-4" />
      </ToolbarButton>
      {isFull && (
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive('codeBlock')}
          title="Code Block"
        >
          <Code className="size-4" />
        </ToolbarButton>
      )}

      <Separator orientation="vertical" className="mx-1 h-6" />

      <Popover open={linkOpen} onOpenChange={setLinkOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={`h-8 w-8 p-0 ${editor.isActive('link') ? 'bg-muted text-foreground' : 'text-muted-foreground'}`}
            title="Add Link"
          >
            <Link2 className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="start">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              setLink()
            }}
            className="flex gap-2"
          >
            <Input
              placeholder="https://..."
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="h-8 text-sm"
            />
            <Button type="submit" size="sm" className="h-8">
              Add
            </Button>
          </form>
        </PopoverContent>
      </Popover>

      {editor.isActive('link') && (
        <ToolbarButton onClick={removeLink} title="Remove Link">
          <Unlink className="size-4" />
        </ToolbarButton>
      )}

      {isFull && (
        <>
          <Separator orientation="vertical" className="mx-1 h-6" />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) uploadImage(file)
            }}
          />
          <ToolbarButton
            onClick={() => fileInputRef.current?.click()}
            title={imageUploading ? 'Uploading…' : 'Insert Image'}
          >
            <ImagePlus className="size-4" />
          </ToolbarButton>

          {enableImageResize && editor.isActive('image') && (
            <div className="flex items-center gap-1 px-1" title="Image width (px)">
              <Input
                type="number"
                min={IMAGE_RESIZE_MIN_WIDTH}
                value={imageWidth}
                onChange={(e) => {
                  const nextValue = e.target.value
                  setImageWidth(nextValue)
                  const width = Number(nextValue)
                  if (nextValue.trim() === '' || !Number.isFinite(width) || width < IMAGE_RESIZE_MIN_WIDTH) return

                  const attrs = editor.getAttributes('image')
                  const currentWidth = parseImageDimension(attrs.width)
                  const currentHeight = parseImageDimension(attrs.height)
                  const nextAttrs: { width: number; height?: number } = { width }

                  if (currentWidth && currentHeight) {
                    nextAttrs.height = Math.round((width * currentHeight) / currentWidth)
                  }

                  const imagePos = editor.state.selection.from
                  editor.chain().updateAttributes('image', nextAttrs).run()
                  applyImageNodeViewSize(editor, imagePos, width, nextAttrs.height)
                }}
                onBlur={() => editor.chain().focus().run()}
                className="h-8 w-20 text-sm"
              />
              <span className="text-xs text-muted-foreground">px</span>
            </div>
          )}

          <Popover open={ytOpen} onOpenChange={setYtOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground"
                title="Embed YouTube"
              >
                <Youtube className="size-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3" align="start">
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  insertYouTube()
                }}
                className="flex gap-2"
              >
                <Input
                  placeholder="https://youtu.be/..."
                  value={ytUrl}
                  onChange={(e) => setYtUrl(e.target.value)}
                  className="h-8 text-sm"
                />
                <Button type="submit" size="sm" className="h-8" disabled={!parseYouTubeId(ytUrl)}>
                  Embed
                </Button>
              </form>
            </PopoverContent>
          </Popover>

          <ToolbarButton
            onClick={() => editor.chain().focus().insertTimeline().run()}
            title="Insert timeline"
          >
            <ListTree className="size-4" />
          </ToolbarButton>

          {editor.isActive('table') ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 bg-muted text-foreground"
                  title="Edit table"
                >
                  <TableIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => editor.chain().focus().addColumnBefore().run()}>
                  Add column before
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().addColumnAfter().run()}>
                  Add column after
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().deleteColumn().run()}>
                  Delete column
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => editor.chain().focus().addRowBefore().run()}>
                  Add row before
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().addRowAfter().run()}>
                  Add row after
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().deleteRow().run()}>
                  Delete row
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeaderRow().run()}>
                  Toggle header row
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => editor.chain().focus().deleteTable().run()}
                  className="text-destructive focus:text-destructive"
                >
                  Delete table
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <ToolbarButton
              onClick={() =>
                editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
              }
              title="Insert table"
            >
              <TableIcon className="size-4" />
            </ToolbarButton>
          )}
        </>
      )}

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        title="Undo"
      >
        <Undo className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        title="Redo"
      >
        <Redo className="size-4" />
      </ToolbarButton>

      {imageError && (
        <p className="ml-2 text-xs text-destructive">{imageError}</p>
      )}
    </div>
  )
}

function parseYouTubeId(url: string): string | null {
  if (!url) return null
  try {
    const u = new URL(url)
    if (u.hostname === 'youtu.be') return u.pathname.slice(1) || null
    if (u.hostname.endsWith('youtube.com')) {
      if (u.pathname === '/watch') return u.searchParams.get('v')
      const parts = u.pathname.split('/').filter(Boolean)
      if (parts[0] === 'embed' || parts[0] === 'shorts') return parts[1] || null
    }
    return null
  } catch {
    return null
  }
}

function parseImageDimension(value: unknown): number | null {
  const numericValue = typeof value === 'number' || typeof value === 'string'
    ? Number(value)
    : NaN

  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null
}

function applyImageNodeViewSize(
  editor: NonNullable<ReturnType<typeof useEditor>>,
  pos: number,
  width: number,
  height?: number,
) {
  const nodeDom = editor.view.nodeDOM(pos)
  if (!(nodeDom instanceof HTMLElement)) return

  const image = nodeDom instanceof HTMLImageElement
    ? nodeDom
    : nodeDom.querySelector('img')

  if (!image) return

  image.style.width = `${width}px`
  if (height) {
    image.style.height = `${height}px`
  } else {
    image.style.removeProperty('height')
  }
}
